import { EditMode } from "@/types/editMode";
import { CreateFilteredElementConfigurationRequest, FilteredElementConfiguration, FilteredElementConfigurationType, ProblemDetails } from "@api/apiClient";
import { useCadMaterials } from "@api/cadMaterials";
import { useCadModelCategories } from "@api/cadModelCategories";
import { useModelFamilies, useModelTypes } from "@api/models";
import { useCreateFilteredElementConfiguration, useFilteredElementConfigurations, useUpdateFilteredElementConfiguration } from "@api/queryBuilders";
import i18n from "@i18n/i18n";
import { Autocomplete, Box, Button, FormHelperText, Grid, Snackbar, TextField, decomposeColor, useTheme } from "@mui/material";
import type { BuilderProps, Config, ImmutableTree, JsonLogicTree } from "@react-awesome-query-builder/mui";
import { Builder, MuiConfig, Utils as QbUtils, Query, ValueSource } from "@react-awesome-query-builder/mui";
import "@react-awesome-query-builder/mui/css/styles.css";
import { observer } from "mobx-react";
import { ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState } from "react";

export interface RuleSetFilterProps {
  filterConfigurationId: string;
  filteredElementConfigurationType: FilteredElementConfigurationType;
  OnClose(): void;
  OnSubmit: (filteredElementConfiguration: FilteredElementConfiguration) => void;
}

export interface ListValue {
  value: string;
  title: string;
}

const queryValue: any = { id: QbUtils.uuid(), type: "group" };

const RuleSetFilterForm = observer((props: RuleSetFilterProps) => {
  const theme = useTheme();
  const [config, setConfig] = useState({
    ...MuiConfig,
    settings: {
      ...MuiConfig.settings,
      theme: {
        mui: {
          palette: theme.palette,
        },
      },
      showNot: false,
      defaultSelectWidth: "300px",
      defaultSearchWidth: "250px",
    },
    fields: {
      CadModelCategoryId: {
        label: i18n.t("rule-set-filter-form.form-elements.cad-model-category"),
        type: "select",
        valueSources: ["value"] as ValueSource[],
        fieldSettings: {
          listValues: [] as ListValue[],
          showSearch: true,
        },
        operators: ["select_any_in"],
      },
      CadMaterial: {
        label: i18n.t("rule-set-filter-form.form-elements.cad-material"),
        type: "select",
        valueSources: ["value"] as ValueSource[],
        fieldSettings: {
          listValues: [] as ListValue[],
          showSearch: true,
        },
        operators: ["select_any_in"],
      },
      CadFamily: {
        label: i18n.t("rule-set-filter-form.form-elements.cad-model-family"),
        type: "select",
        valueSources: ["value"] as ValueSource[],
        fieldSettings: {
          listValues: [] as ListValue[],
          showSearch: true,
        },
        operators: ["select_any_in"],
      },
      CadName: {
        label: i18n.t("rule-set-filter-form.form-elements.model-types"),
        type: "select",
        valueSources: ["value"] as ValueSource[],
        fieldSettings: {
          listValues: [] as ListValue[],
          showSearch: true,
        },
        operators: ["select_any_in"],
      },
      CustomCadMaterial: {
        label: i18n.t("rule-set-filter-form.form-elements.cad-material-text"),
        type: "multiselect",
        fieldSettings: {
          listValues: [] as ListValue[],
          showSearch: true,
          allowCustomValues: true,
        },
      },
    },
  });

  const [editMode, setEditMode] = useState(EditMode.Add);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isConfigRequired, setIsConfigRequired] = useState(false);
  const [isDuplicateRuleSet, setisDuplicateRuleSet] = useState(false);
  const [open, setOpen] = useState(false);
  const [elementFilterConfigurationId, setElementFilterConfigurationId] = useState(null);
  const [selectedCadFamilies, setSelectedCadFamilies] = useState<string[]>([]);
  const [allModelTypes, setAllModelTypes] = useState<any[]>([]);
  const [currentConfig, setCurrentConfig] = useState(config);
  const [isAddingRule, setIsAddingRule] = useState(false);
  const queryBuilderRef = useRef<any>(null);

  const createFilteredElementConfiguration = useCreateFilteredElementConfiguration();
  const updateFilteredElementConfiguration = useUpdateFilteredElementConfiguration();
  const ruleSets = useFilteredElementConfigurations(props.filteredElementConfigurationType);
  const cadModelCategories = useCadModelCategories();
  const cadMaterials = useCadMaterials();
  const cadFamilies = useModelFamilies();
  const modelTypes = useModelTypes();

  const [formData, setFormData] = useState({
    name: "",
    oldName: "",
    description: "",
    filteredElementConfiguration: null,
  });

  const [queryState, setQueryState] = useState<any>({
    tree: QbUtils.loadTree(queryValue),
    config,
  });

  // Function to get filtered model type values based on selected CadFamily
  const getFilteredModelTypeValues = useCallback(
    (selectedFamilies: string[]) => {
      if (!allModelTypes || selectedFamilies.length === 0) {
        return [];
      }

      console.log("Filtering model types for families:", selectedFamilies);
      console.log("All model types:", allModelTypes);

      const filtered = allModelTypes
        .filter((t: any) => selectedFamilies.includes(t.cadFamily))
        .map((t: any) => {
          const cadName = String(t?.cadName ?? "").trim();
          const display = t?.display ?? `${String(t?.cadFamily ?? "").trim()}${String(t?.cadFamily ?? "").trim() ? " - " : ""}${cadName}`;
          // IMPORTANT: value must be cadName; title must be display
          return { value: cadName, title: display };
        })
        .filter((m: any) => m.value && m.value.length > 0)
        .reduce((acc: ListValue[], cur: any) => {
          if (!acc.some((a) => a.value === cur.value && a.title === cur.title)) {
            acc.push({ value: cur.value, title: cur.title });
          }
          return acc;
        }, [])
        .sort((a, b) => a.title.localeCompare(b.title));

      console.log("Filtered model types:", filtered);
      return filtered;
    },
    [allModelTypes],
  );

  // Function to get unique CadName values from filtered model types
  const getFilteredCadNameValues = useCallback(
    (selectedFamilies: string[]) => {
      if (!allModelTypes || selectedFamilies.length === 0) {
        return [];
      }

      const cadNames: { cadName: string; display: string }[] = Array.from(
        new Set(
          allModelTypes
            .filter((t: any) => {
              const family = String(t?.cadFamily ?? "")
                .trim()
                .toLowerCase();
              return selectedFamilies.some(
                (sf: any) =>
                  String(sf ?? "")
                    .trim()
                    .toLowerCase() === family,
              );
            })
            // Map to objects so we can preserve both cadName and display
            .map((t: any) => {
              const cadName = String(t?.cadName ?? "").trim();
              const display = t?.display ?? `${String(t?.cadFamily ?? "").trim()}${String(t?.cadFamily ?? "").trim() ? " - " : ""}${cadName}`;
              return `${cadName}___SEP___${display}`;
            }),
        ),
      )
        .map((s) => {
          const [cadName, display] = String(s).split("___SEP___");
          return { cadName, display };
        })
        .filter((n) => n.cadName.length > 0)
        .sort((a, b) => a.display.localeCompare(b.display));

      // Return as listValues: value = cadName, title = display
      return cadNames.map((item) => ({ value: item.cadName, title: item.display })).sort((a, b) => a.title.localeCompare(b.title));
    },
    [allModelTypes],
  );

  // Function to extract CadFamily selections from the query tree
  const extractCadFamilySelections = useCallback((tree: any): string[] => {
    const selections: string[] = [];

    const traverse = (node: any) => {
      console.log("Traversing node:", node);

      if (node.type === "rule" && node.properties.field === "CadFamily" && node.properties.value) {
        console.log("Found CadFamily rule node:", node);

        // Handle the nested array structure
        if (Array.isArray(node.properties.value)) {
          node.properties.value.forEach((valueItem: any) => {
            if (Array.isArray(valueItem)) {
              // If it's an array of arrays, flatten it
              selections.push(...valueItem);
            } else {
              // If it's a simple array, add the values
              selections.push(valueItem);
            }
          });
        } else {
          // Single value
          selections.push(node.value);
        }
      }

      // Handle the specific structure you showed - children1 property
      if (node.children1 && Array.isArray(node.children1)) {
        console.log("Found children1 array:", node.children1);
        node.children1.forEach(traverse);
      }

      // Handle regular children property
      if (node.children && Array.isArray(node.children)) {
        console.log("Found children array:", node.children);
        node.children.forEach(traverse);
      }
    };

    traverse(tree);

    // Remove duplicates and filter out undefined/null values
    const uniqueSelections = [...new Set(selections)].filter((val) => val !== undefined && val !== null && val !== "");

    console.log("Extracted CadFamily selections:", uniqueSelections);
    return uniqueSelections;
  }, []);

  // Handle CadFamily selection changes
  const handleCadFamilyChange = useCallback(
    (selectedFamilies: string[]) => {
      console.log("Handling CadFamily change:", selectedFamilies);
      setSelectedCadFamilies(selectedFamilies);

      const filteredModelTypeValues = getFilteredModelTypeValues(selectedFamilies);
      const filteredCadNameValues = getFilteredCadNameValues(selectedFamilies);

      console.log("Updating config with filtered values:", {
        cadNameValues: filteredCadNameValues,
        modelTypeValues: filteredModelTypeValues,
      });

      const newConfig = {
        ...currentConfig,
        fields: {
          ...currentConfig.fields,
          CadName: {
            ...currentConfig.fields.CadName,
            fieldSettings: {
              ...currentConfig.fields.CadName.fieldSettings,
              listValues: filteredCadNameValues,
            },
          },
        },
      };

      setCurrentConfig(newConfig);
      setConfig(newConfig);
    },
    [getFilteredModelTypeValues, getFilteredCadNameValues, currentConfig],
  );

  // Store all model types when they load
  useEffect(() => {
    if (modelTypes.data) {
      setAllModelTypes(modelTypes.data);
    }
  }, [modelTypes.data]);

  // useEffect to monitor CadFamily config changes and update model types
  useEffect(() => {
    if (allModelTypes.length > 0 && !isAddingRule) {
      console.log("CadFamily config changed, checking for selections...");

      // Extract current CadFamily selections from the query tree
      const jsonTree = QbUtils.getTree(queryState.tree);
      const cadFamilySelections = extractCadFamilySelections(jsonTree);

      console.log("Current CadFamily selections from config monitoring:", cadFamilySelections);

      // Only update if the selections have actually changed
      if (JSON.stringify(cadFamilySelections.sort()) !== JSON.stringify(selectedCadFamilies.sort())) {
        console.log("CadFamily selections changed in config monitoring:", cadFamilySelections);
        handleCadFamilyChange(cadFamilySelections);
      }
    }
  }, [config.fields.CadFamily, allModelTypes, queryState.tree, selectedCadFamilies, extractCadFamilySelections, handleCadFamilyChange, isAddingRule]);

  // useEffect to monitor query tree changes for CadFamily selections
  useEffect(() => {
    if (queryState.tree && allModelTypes.length > 0 && !isAddingRule) {
      const jsonTree = QbUtils.getTree(queryState.tree);
      const cadFamilySelections = extractCadFamilySelections(jsonTree);

      console.log("Query tree changed, extracted families:", cadFamilySelections);

      // Only update if the selections have actually changed
      if (JSON.stringify(cadFamilySelections.sort()) !== JSON.stringify(selectedCadFamilies.sort())) {
        console.log("CadFamily selections changed from query tree:", cadFamilySelections);
        handleCadFamilyChange(cadFamilySelections);
      }
    }
  }, [queryState.tree, allModelTypes, selectedCadFamilies, extractCadFamilySelections, handleCadFamilyChange, isAddingRule]);

  useEffect(() => {
    console.log(`cadModelCategories: `, cadModelCategories.data);
    console.log(`cadMaterials: `, cadMaterials.data);

    const cadModelCategoryValues: any[] = (cadModelCategories.data?.map((x) => ({ value: x.cadModelCategoryId, title: x.label })) || []).sort((a: any, b: any) =>
      a.title.localeCompare(b.title),
    );
    const cadMaterialValues = (cadMaterials.data?.map((x) => ({ value: x, title: x })) || []).sort((a: any, b: any) => a.title.localeCompare(b.title)) || [];
    const cadFamilyValues = (cadFamilies.data?.map((x) => ({ value: x, title: x })) || []).sort((a: any, b: any) => a.title.localeCompare(b.title)) || [];

    // Initial model type values (unfiltered)
    const modelTypeValues: ListValue[] = (modelTypes.data || [])
      .map((t: any) => {
        const cadName = String(t?.cadName ?? "").trim();
        const display = t?.display ?? `${String(t?.cadFamily ?? "").trim()}${String(t?.cadFamily ?? "").trim() ? " - " : ""}${cadName}`;
        // Value must be cadName; title must be display
        return { value: cadName, title: display };
      })
      .filter((m: any) => m.value && m.value.length > 0)
      .reduce((acc: ListValue[], cur: any) => {
        if (!acc.some((a) => a.value === cur.value && a.title === cur.title)) {
          acc.push({ value: cur.value, title: cur.title });
        }
        return acc;
      }, [])
      .sort((a, b) => a.title.localeCompare(b.title));

    if (cadMaterials.data?.length !== undefined && cadMaterials?.data?.length > 0 && props.filterConfigurationId) {
      const ruleSet = ruleSets?.data?.find((x) => x.filteredElementConfigurationId === props?.filterConfigurationId);
      setupFormDataForEdit({ name: ruleSet?.name, description: ruleSet?.description, filteredElementConfiguration: ruleSet });
    }

    const newConfig = {
      ...config,
      fields: {
        ...config.fields,
        CadModelCategoryId: {
          ...config.fields.CadModelCategoryId,
          fieldSettings: {
            ...config.fields.CadModelCategoryId.fieldSettings,
            listValues: cadModelCategoryValues,
          },
        },
        CadMaterial: {
          ...config.fields.CadMaterial,
          fieldSettings: {
            ...config.fields.CadMaterial.fieldSettings,
            listValues: cadMaterialValues,
          },
        },
        CadFamily: {
          ...config.fields.CadFamily,
          fieldSettings: {
            ...config.fields.CadFamily.fieldSettings,
            listValues: cadFamilyValues,
          },
        },
        CadName: {
          ...config.fields.CadName,
          fieldSettings: {
            ...config.fields.CadName.fieldSettings,
            listValues: modelTypeValues,
          },
        },
      },
    };

    setCurrentConfig(newConfig);
    setConfig(newConfig);
  }, [cadModelCategories.data, cadMaterials.data, props.filterConfigurationId, cadFamilies.data, modelTypes.data]);

  useEffect(() => {
    if (props.filterConfigurationId) {
      const ruleSet = ruleSets?.data?.find((x) => x.filteredElementConfigurationId === props?.filterConfigurationId);
      setupFormDataForEdit({ name: ruleSet?.name, description: ruleSet?.description, filteredElementConfiguration: ruleSet });
    }
  }, [props.filterConfigurationId]);

  const onQueryChange = useCallback(
    (immutableTree: ImmutableTree, config: Config) => {
      console.log(`onQueryChange: `, immutableTree, config);
      setQueryState({ ...queryState, tree: immutableTree, config });

      // Only process CadFamily filtering if we're not in the middle of adding a rule
      if (!isAddingRule) {
        const jsonTree = QbUtils.getTree(immutableTree);

        // Immediately check for CadFamily selections
        const cadFamilySelections = extractCadFamilySelections(jsonTree);
        console.log("Immediate CadFamily selections from onQueryChange:", cadFamilySelections);

        if (cadFamilySelections.length > 0) {
          handleCadFamilyChange(cadFamilySelections);
        } else if (selectedCadFamilies.length > 0) {
          // If no families selected, reset to show all
          handleCadFamilyChange([]);
        }
      }
    },
    [config, extractCadFamilySelections, handleCadFamilyChange, selectedCadFamilies, isAddingRule],
  );

  const renderBuilder = useCallback((props: BuilderProps) => {
    // Override the addRule function to set the isAddingRule flag
    const originalAddRule = props.actions?.addRule;
    const enhancedActions = {
      ...props.actions,
      addRule: (...args: any[]) => {
        console.log("Adding new rule...");
        setIsAddingRule(true);

        // Call the original addRule function if it's defined as a function
        let result: any;
        if (typeof originalAddRule === "function") {
          // Use apply to pass an array of arguments without using the spread operator
          result = (originalAddRule as Function).apply(undefined, args);
        }

        // Reset the flag after a short delay to allow the rule to be added
        setTimeout(() => {
          setIsAddingRule(false);
          console.log("Rule addition completed");
        }, 100);

        return result;
      },
    };

    return (
      <div className="query-builder-container">
        <div className="query-builder qb-lite">
          <Builder {...props} actions={enhancedActions} />
        </div>
      </div>
    );
  }, []);

  const handleRuleSetChange = (_event: any, value: any) => {
    if (!value) {
      ResetFormData();
    } else {
      setisDuplicateRuleSet(false);
      setIsConfigRequired(false);
      const ruleSet = ruleSets?.data?.find((x) => x.filteredElementConfigurationId === value.filteredElementConfigurationId);
      setupFormDataForEdit({ name: ruleSet?.name, description: ruleSet?.description, filteredElementConfiguration: ruleSet });
    }
  };

  const ResetFormData = () => {
    setFormData({ name: "", oldName: "", description: "", filteredElementConfiguration: null });
    setEditMode(EditMode.Add);
    setElementFilterConfigurationId(null);
    setSelectedCadFamilies([]);
    setIsAddingRule(false);
    setQueryState((prevState: any) => ({ ...prevState, tree: QbUtils.loadTree(queryValue), config }));
  };

  const setupFormDataForEdit = (data: any) => {
    setFormData({
      ...formData,
      name: data?.name || formData.name,
      oldName: data?.name,
      description: data?.description || formData.description,
      filteredElementConfiguration: data?.filteredElementConfiguration || formData.filteredElementConfiguration,
    } as any);
    data !== undefined ? setEditMode(EditMode.Edit) : setEditMode(EditMode.Add);
    setElementFilterConfigurationId(data?.filteredElementConfiguration?.filteredElementConfigurationId || null);
    setQueryState({ ...queryState, tree: QbUtils.loadFromJsonLogic(JSON.parse(data?.filteredElementConfiguration.config || "{}") as JsonLogicTree, queryState.config) });
  };

  const handleSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setisDuplicateRuleSet(false);
    try {
      if (!isDuplicateRule()) {
        setIsSubmitted(true);
        const jsonLogicResult = QbUtils.jsonLogicFormat(queryState.tree, config);
        const ruleSetFilterRequest: any = new CreateFilteredElementConfigurationRequest({
          name: formData.name,
          description: formData.description,
          filteredElementConfigurationType: FilteredElementConfigurationType.AddNonModelledElement,
          configuration: JSON.stringify(jsonLogicResult.logic),
        });
        const rulset =
          editMode === EditMode.Add
            ? await createFilteredElementConfiguration.mutateAsync(ruleSetFilterRequest)
            : await updateFilteredElementConfiguration.mutateAsync({
                filteredElementConfigurationId: elementFilterConfigurationId,
                updateFilteredElementConfigurationRequest: ruleSetFilterRequest,
              } as any);
        await ruleSets.refetch();
        props.OnSubmit(rulset);
        setOpen(true);
        setIsSubmitted(false);
      }
    } catch (error) {
      const err = error as ProblemDetails;
      if (err?.errors?.Configuration?.[0] === "The Configuration field is required.") {
        setIsConfigRequired(true);
      }

      console.error(error);
    }
  };

  //Returns true if rule sets already has a rule which is being created, and false otherwise
  const isDuplicateRule = (): boolean => {
    if (editMode === EditMode.Add) {
      const isduplicate = ruleSets?.data?.find((x) => x.name?.toLocaleLowerCase().trim() === formData.name.toLocaleLowerCase().trim());
      if (isduplicate !== undefined) {
        setisDuplicateRuleSet(true);
        return true;
      }
      return false;
    }

    if (editMode === EditMode.Edit) {
      // In Edit Mode if user Modifies the Rule Set name,only then check for duplicate rule
      if (formData.oldName !== formData.name) {
        const isduplicate = ruleSets?.data?.find((x) => x.name?.toLocaleLowerCase().trim() === formData.name.toLocaleLowerCase().trim());
        if (isduplicate !== undefined) {
          setisDuplicateRuleSet(true);
          return true;
        }
        return false;
      }
    }
    return false;
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [event.target.name]: event.target.value } as any);
    console.log(event.target.value);
  };

  console.log(`theme: `, theme);
  console.log(`config: `, config);
  console.log(`decomposeColor: `, decomposeColor(theme.palette.text.primary));
  console.log(`selectedCadFamilies: `, selectedCadFamilies);
  console.log(`filtered model types: `, getFilteredModelTypeValues(selectedCadFamilies));
  console.log(`isAddingRule: `, isAddingRule);

  return (
    <>
      <form onSubmit={handleSave}>
        <Grid mt={1} container spacing={2}>
          <Grid size={12}>
            <Grid container spacing={2} sx={{ alignItems: "center" }}>
              <Grid size="grow">
                <Autocomplete
                  id="rule-set-filter-select"
                  options={ruleSets?.data || []}
                  getOptionLabel={(option) => option?.name || ""}
                  getOptionKey={(option) => option?.filteredElementConfigurationId || ""}
                  autoHighlight
                  defaultValue={formData.filteredElementConfiguration || null}
                  value={formData.filteredElementConfiguration}
                  sx={{ width: "100%" }}
                  onChange={handleRuleSetChange}
                  renderInput={(params) => <TextField {...params} label={i18n.t("rule-set-filter-form.rule-set-filter-select.placeholder")} />}
                />
              </Grid>
              <Grid size="auto" mt={-2.5}>
                <Button variant="outlined" onClick={() => ResetFormData()}>
                  {i18n.t("rule-set-filter-form.add-filter")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <TextField
              label={i18n.t("rule-set-filter-form.form-elements.name")}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              variant="outlined"
              required
              fullWidth
              error={isDuplicateRuleSet}
              helperText={isDuplicateRuleSet ? i18n.t("rule-set-filter-form.error.duplicate-rule-set") : ""}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label={i18n.t("rule-set-filter-form.form-elements.description")}
              name="description"
              onChange={handleInputChange}
              value={formData.description}
              variant="outlined"
              required
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <Box
              sx={{
                "& *": { color: theme.palette.text.primary },
                "& .query-builder": { margin: "0 !important", backgroundColor: "unset" },
                "& .query-builder .group-or-rule": { backgroundColor: "unset", borderColor: theme.palette.divider },
                "& .query-builder .group-container": { backgroundColor: "unset", padding: 0 },
                "& .query-builder .group--actions, & .query-builder .group--actions--tr": { opacity: "1 !important" },
                "& .query-builder .rule ": { display: "block" },
                "& .rule--header": { float: "right", marginTop: "calc(-58px)" },
                "& .query-builder .rule--body--wrapper": { marginTop: "20px" },
              }}
            >
              <Query {...config} value={queryState.tree} onChange={onQueryChange} renderBuilder={renderBuilder} />
              {isConfigRequired && isSubmitted && (
                <FormHelperText error={true} style={{ marginLeft: "14px" }}>
                  {i18n.t("rule-set-filter-form.error.config-mandatory")}
                </FormHelperText>
              )}
            </Box>
          </Grid>
          <Grid size={12} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ alignSelf: "center", color: theme.palette.text.secondary }}>{i18n.t("rule-set-filter-form.model-family-note")}</div>
            <div>
              <Button onClick={props.OnClose} variant="outlined">
                {i18n.t("rule-set-filter-form.cancel")}
              </Button>
              <Button autoFocus variant="contained" type="submit" sx={{ marginLeft: 1 }}>
                {i18n.t("rule-set-filter-form.save")}
              </Button>
            </div>
          </Grid>
        </Grid>
      </form>
      <Snackbar
        open={open}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        message={editMode === EditMode.Add ? i18n.t("rule-set-filter-form.created-success") : i18n.t("rule-set-filter-form.edited-success")}
        onClose={() => setOpen(false)}
      />
    </>
  );
});

export default RuleSetFilterForm;
